import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  AuthUserState,
  PayloadLoginAndRefreshTokenAction,
  RepresentativeEmpty,
  RepresentativeLocalStorage,
  RepresentativeLocalStorageEmpty,
  TokenLocalStorage,
  TokenLocalStorageEmpty,
} from 'domain/models/authUser'
import { RecoverPasswordRepresentativeCommandResponse } from 'domain/entities/authUser'
import { accessTokenKey, representativeKey } from 'presentation/constant/localStorageKeys'
import { RootState } from 'presentation/redux/reducers'

const userInfo: RepresentativeLocalStorage =
  JSON.parse(sessionStorage.getItem(representativeKey) as string) || RepresentativeLocalStorageEmpty
const token: TokenLocalStorage =
  JSON.parse(sessionStorage.getItem(accessTokenKey) as string) || TokenLocalStorageEmpty

const initialState: AuthUserState = {
  logged: userInfo.logged,
  user: userInfo.representative,
  token: token.accessToken,
  refreshToken: userInfo.refreshToken,
  isLoading: false,
  error: null,
  refreshExpiresIn: userInfo.refreshExpiresIn,
  accessExpiresIn: token.accessExpiresIn,
  userForgotPassword: null,
}
export const authUserSlice = createSlice({
  name: 'authUser',
  initialState,
  reducers: {
    loginStart: (state: AuthUserState) => {
      state.isLoading = true
      state.error = null
    },
    loginSuccess: (
      state: AuthUserState,
      action: PayloadAction<PayloadLoginAndRefreshTokenAction>,
    ) => {
      state.isLoading = false
      state.logged = action.payload.logged
      state.user = action.payload.representative
      state.token = action.payload.token
      state.refreshToken = action.payload.refreshToken as string
      state.refreshExpiresIn = action.payload.refreshExpiresIn as number
      state.accessExpiresIn = action.payload.accessExpiresIn as number
    },
    loginError: (state: AuthUserState, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },
    logoutStart: (state: AuthUserState) => {
      state.isLoading = true
      state.error = null
    },
    logoutSuccess: (state: AuthUserState) => {
      state.isLoading = false
      state.logged = false
      state.user = RepresentativeEmpty
      state.token = ''
      state.refreshToken = ''
      state.accessExpiresIn = 0
      state.refreshExpiresIn = 0
    },
    logoutError: (state: AuthUserState, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },
    firstChangePassStart: (state: AuthUserState) => {
      state.isLoading = true
      state.error = null
    },
    firstChangePassSuccess: (state: AuthUserState) => {
      state.isLoading = false
      state.user = { ...state.user, ...{ firstLogin: false } }
    },
    firstChangePassError: (state: AuthUserState, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },
    refreshTokenSuccess: (
      state: AuthUserState,
      action: PayloadAction<PayloadLoginAndRefreshTokenAction>,
    ) => {
      state.isLoading = false
      state.error = null
      state.logged = action.payload.logged
      state.user = action.payload.representative
      state.token = action.payload.token
      state.refreshToken = action.payload.refreshToken as string
      state.accessExpiresIn = action.payload.accessExpiresIn as number
      state.refreshExpiresIn = action.payload.refreshExpiresIn as number
    },
    recoverPasswordStart: (state: AuthUserState) => {
      state.isLoading = true
      state.error = null
      state.userForgotPassword = null
    },
    recoverPasswordSuccess: (
      state: AuthUserState,
      action: PayloadAction<RecoverPasswordRepresentativeCommandResponse>,
    ) => {
      state.isLoading = false
      state.userForgotPassword = action.payload
    },
    recoverPasswordError: (state: AuthUserState, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },
    resetPasswordStart: (state: AuthUserState) => {
      state.isLoading = true
      state.error = null
    },
    resetPasswordSuccess: (state: AuthUserState) => {
      state.isLoading = false
    },
    resetPasswordError: (state: AuthUserState, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },
    clearErrorAuth: (state: AuthUserState) => {
      state.error = null
    },
  },
})

export const authUserState = (state: RootState) => state.authUser

export const {
  loginStart,
  loginError,
  loginSuccess,
  logoutStart,
  logoutSuccess,
  logoutError,
  firstChangePassStart,
  firstChangePassSuccess,
  firstChangePassError,
  refreshTokenSuccess,
  recoverPasswordError,
  recoverPasswordStart,
  recoverPasswordSuccess,
  clearErrorAuth,
  resetPasswordError,
  resetPasswordSuccess,
  resetPasswordStart,
} = authUserSlice.actions

export default authUserSlice.reducer
